import React, { useState, useEffect } from 'react';
import './usertools-css/PotassiumSodiumCalculator.css';
import '../../styles/components/ainutritionlandingpage.css';


const potassiumAITable = {
  // mg/day — using the simplified table the app already had (adult values align with 2019 update)
  '1-3y': 2000,
  '4-8y': 2300,
  '9-13y_male': 2500,
  '9-13y_female': 2300,
  '14-18y_male': 3000,
  '14-18y_female': 2300,
  '19+y_male': 3400,
  '19+y_female': 2600,
  'pregnant_14-18': 2600,
  'pregnant_19+': 2900,
  'lactating_14-18': 2500,
  'lactating_19+': 2800,
};

const sodiumCDRRTable = {
  // mg/day — Chronic Disease Risk Reduction Intake
  '1-3y': 1200,
  '4-8y': 1500,
  '9-13y': 1800,
  '14-18y': 2300,
  '19+y': 2300,
};

// ---------- Food K/Na values (per common serving; simplified) ----------
const foodNaKValues = {
  // High K foods
  banana_medium: { k: 422, na: 1 },
  avocado_half: { k: 345, na: 5 },
  potato_baked_medium: { k: 925, na: 17 },
  spinach_cooked_cup: { k: 840, na: 125 },
  lentils_cooked_cup: { k: 730, na: 4 },
  yogurt_plain_cup: { k: 530, na: 170 },
  salmon_3oz: { k: 330, na: 50 },

  // High Na foods
  canned_soup_cup: { na: 800, k: 150 },
  bread_slice: { na: 150, k: 40 },
  processed_meat_2oz: { na: 600, k: 100 },
  cheese_slice_1oz: { na: 400, k: 30 },
  fast_food_burger: { na: 1000, k: 240 },
  pizza_slice: { na: 700, k: 150 },
  chips_small_bag_1oz: { na: 180, k: 300 },
  soy_sauce_tbsp: { na: 900, k: 50 },
};

// ---------- Assumptions / constants ----------
const RESTAURANT_MEAL_NA_ADD_AVG = 500; // mg per restaurant/takeaway meal (conservative)
const TABLE_SALT_NA_ADD_ESTIMATE = 400; // mg if the user usually adds salt at table

// Sweat sodium replacement (need), not intake:
const SWEAT_NA_REPLACEMENT_MG_PER_HOUR = 900; // mid-range heuristic (~950 mg/L at ~1 L/hr); highly individual

// Example electrolyte powder (per serving):
const electrolytePowderExample = { na: 500, k: 250 }; // mg per scoop

// KCl salt-sub potency (corrected): ~650 mg K per 1/4 tsp
const SALT_SUB_POTASSIUM_PER_QUARTER_TSP_MG = 650;

// Safety thresholds (educational; individual risk varies):
const safetyThresholds = {
  hyperkalemia_risk_k_mg: 4700,     // caution threshold if CKD/ACE/ARB/K-sparing present
  hyponatremia_risk_na_mg: 1500,    // caution if on loop/thiazide diuretics
  excess_sodium_warn_mg: 5000,      // very high daily sodium flag
  ckd_k_supplement_cap_mg: 1000,    // rough ceiling for K supplements in CKD3+ (educational)
};

// ---------- Helpers ----------
const toNumber = (val) => {
  const n = Number.parseFloat(String(val));
  return Number.isFinite(n) ? n : 0;
};

function getPotassiumAIKey(ageNum, sex, isPregnant, isLactating) {
  if (ageNum <= 3) return '1-3y';
  if (ageNum <= 8) return '4-8y';
  if (ageNum <= 13) return sex === 'male' ? '9-13y_male' : '9-13y_female';
  if (ageNum <= 18) {
    if (isPregnant) return 'pregnant_14-18';
    if (isLactating) return 'lactating_14-18';
    return sex === 'male' ? '14-18y_male' : '14-18y_female';
  }
  if (ageNum >= 19) {
    if (isPregnant) return 'pregnant_19+';
    if (isLactating) return 'lactating_19+';
    return sex === 'male' ? '19+y_male' : '19+y_female';
  }
  return '19+y_female';
}

function getSodiumCDRRKey(ageNum) {
  if (ageNum <= 3) return '1-3y';
  if (ageNum <= 8) return '4-8y';
  if (ageNum <= 13) return '9-13y';
  if (ageNum <= 18) return '14-18y';
  return '19+y';
}

function estimateSweatNaReplacement(activityLevel) {
  if (activityLevel === 'moderate') return SWEAT_NA_REPLACEMENT_MG_PER_HOUR * 1.5; // ~1–2h
  if (activityLevel === 'high') return SWEAT_NA_REPLACEMENT_MG_PER_HOUR * 2.5; // >2h
  return 0; // <1h
}

const fmt0 = (n) => (Number.isFinite(n) ? Math.round(n).toLocaleString() : '—');
const fmt2 = (n) => (Number.isFinite(n) ? n.toFixed(2) : '∞');

// ---------- Component ----------
function PotassiumSodiumCalculator() {
  // Demographics
  const [age, setAge] = useState('');
  const [sex, setSex] = useState('');
  const [isPregnant, setIsPregnant] = useState(false);
  const [isLactating, setIsLactating] = useState(false);

  // Health flags
  const [healthFlags, setHealthFlags] = useState({
    hypertension: false,
    ckdStage3Plus: false,
    heartFailure: false,
    aceiArbMed: false,
    kSparingDiureticMed: false,
    loopThiazideDiureticMed: false,
    nsaidsMed: false,
  });

  // Activity / sweat
  const [activityLevel, setActivityLevel] = useState('low'); // 'low' | 'moderate' | 'high'

  // Diet log
  const [dietLog, setDietLog] = useState({
    servings_banana: '',
    servings_avocado: '',
    servings_potato: '',
    servings_spinach: '',
    servings_legumes: '',
    servings_dairy: '',

    servings_canned_soup: '',
    servings_bread: '',
    servings_processed_meat: '',
    servings_cheese: '',
    servings_fast_food_meal: '',
    servings_salty_snacks: '',

    meals_eaten_out: '',
  });

  // Supplements / habits
  const [takesElectrolyteSupp, setTakesElectrolyteSupp] = useState(false);
  const [electrolyteServings, setElectrolyteServings] = useState('');
  const [usesSaltSubstitute, setUsesSaltSubstitute] = useState(false);
  const [saltSubServingsPerDay, setSaltSubServingsPerDay] = useState(''); // # of 1/4 tsp
  const [addsTableSalt, setAddsTableSalt] = useState(false);

  // Optional measurements
  const [serumNa, setSerumNa] = useState(''); // mmol/L
  const [serumK, setSerumK] = useState('');   // mmol/L
  const [bpSys, setBpSys] = useState('');
  const [bpDia, setBpDia] = useState('');

  // Results
  const [calculationResult, setCalculationResult] = useState(null);
  const [showResults, setShowResults] = useState(false);

  // Derived
  const ageNum = Number.parseInt(age, 10);
  const isFemale = sex === 'female';

  useEffect(() => {
    if (sex === 'male' || ageNum < 12 || ageNum > 55) {
      setIsPregnant(false);
      setIsLactating(false);
    }
  }, [sex, ageNum]);

  const handleHealthFlagChange = (e) => {
    const { name, checked } = e.target;
    setHealthFlags((prev) => ({ ...prev, [name]: checked }));
  };

  const handleDietChange = (e) => {
    const { name, value } = e.target;
    setDietLog((prev) => ({ ...prev, [name]: value }));
  };

  const calculatePotassiumSodiumBalance = (e) => {
    e.preventDefault();
    setShowResults(false);
    setCalculationResult(null);

    const explanation = [];
    const safetyFlags = [];
    const suggestions = { foodSwaps: [], general: [], supplement: [] };

    if (Number.isNaN(ageNum) || !sex) {
      alert('Please fill in required fields: Age and Sex.');
      return;
    }

    // 1) Targets
    const kKey = getPotassiumAIKey(ageNum, sex, isPregnant, isLactating);
    const naKey = getSodiumCDRRKey(ageNum);
    const targetK_AI = potassiumAITable[kKey] ?? (sex === 'male' ? 3400 : 2600);
    const targetNa_CDRR = sodiumCDRRTable[naKey] ?? 2300;

    explanation.push(`Potassium AI Target (${kKey.replaceAll('_', ' ')}): ${targetK_AI} mg/day.`);
    explanation.push(`Sodium CDRR Target (${naKey.replaceAll('_', ' ')}): ${targetNa_CDRR} mg/day (aim to stay below this).`);

    // 2) Intakes
    const getDietVal = (key) => toNumber(dietLog[key]);

    let foodK = 0;
    let foodNa = 0;

    // K from food (including some "salty" foods that still have K)
    foodK += getDietVal('servings_banana') * foodNaKValues.banana_medium.k;
    foodK += getDietVal('servings_avocado') * foodNaKValues.avocado_half.k;
    foodK += getDietVal('servings_potato') * foodNaKValues.potato_baked_medium.k;
    foodK += getDietVal('servings_spinach') * foodNaKValues.spinach_cooked_cup.k;
    foodK += getDietVal('servings_legumes') * foodNaKValues.lentils_cooked_cup.k;
    foodK += getDietVal('servings_dairy') * foodNaKValues.yogurt_plain_cup.k;

    foodK += getDietVal('servings_canned_soup') * foodNaKValues.canned_soup_cup.k;
    foodK += getDietVal('servings_bread') * foodNaKValues.bread_slice.k;
    foodK += getDietVal('servings_processed_meat') * foodNaKValues.processed_meat_2oz.k;
    foodK += getDietVal('servings_cheese') * foodNaKValues.cheese_slice_1oz.k;
    foodK += getDietVal('servings_fast_food_meal') * foodNaKValues.fast_food_burger.k;
    foodK += getDietVal('servings_salty_snacks') * foodNaKValues.chips_small_bag_1oz.k;

    // Na from food
    foodNa += getDietVal('servings_banana') * foodNaKValues.banana_medium.na;
    foodNa += getDietVal('servings_avocado') * foodNaKValues.avocado_half.na;
    foodNa += getDietVal('servings_potato') * foodNaKValues.potato_baked_medium.na;
    foodNa += getDietVal('servings_spinach') * foodNaKValues.spinach_cooked_cup.na;
    foodNa += getDietVal('servings_legumes') * foodNaKValues.lentils_cooked_cup.na;
    foodNa += getDietVal('servings_dairy') * foodNaKValues.yogurt_plain_cup.na;

    foodNa += getDietVal('servings_canned_soup') * foodNaKValues.canned_soup_cup.na;
    foodNa += getDietVal('servings_bread') * foodNaKValues.bread_slice.na;
    foodNa += getDietVal('servings_processed_meat') * foodNaKValues.processed_meat_2oz.na;
    foodNa += getDietVal('servings_cheese') * foodNaKValues.cheese_slice_1oz.na;
    foodNa += getDietVal('servings_fast_food_meal') * foodNaKValues.fast_food_burger.na;
    foodNa += getDietVal('servings_salty_snacks') * foodNaKValues.chips_small_bag_1oz.na;

    explanation.push(`Estimated intake from logged foods: K = ${fmt0(foodK)} mg, Na = ${fmt0(foodNa)} mg.`);

    // Restaurant sodium (intake)
    const mealsOut = getDietVal('meals_eaten_out');
    const restaurantNaAdj = mealsOut * RESTAURANT_MEAL_NA_ADD_AVG;
    foodNa += restaurantNaAdj;
    if (mealsOut > 0) explanation.push(`+${fmt0(restaurantNaAdj)} mg Na estimated from ${mealsOut} restaurant meal(s).`);

    // Sweat sodium: replacement need (NOT counted as intake)
    const sweatNaReplacement = estimateSweatNaReplacement(activityLevel);
    if (sweatNaReplacement > 0) {
      explanation.push(`Estimated Na replacement need from sweat: ~${fmt0(sweatNaReplacement)} mg/day (not counted as intake).`);
    }

    // Table salt (intake)
    const tableSaltNaAdj = addsTableSalt ? TABLE_SALT_NA_ADD_ESTIMATE : 0;
    foodNa += tableSaltNaAdj;
    if (tableSaltNaAdj > 0) explanation.push(`+${fmt0(tableSaltNaAdj)} mg Na for estimated table salt use.`);

    // Supplements
    let suppK = 0;
    let suppNa = 0;

    const elecServings = toNumber(electrolyteServings);
    if (takesElectrolyteSupp && elecServings > 0) {
      suppNa += elecServings * electrolytePowderExample.na;
      suppK += elecServings * electrolytePowderExample.k;
      explanation.push(
        `Electrolyte supplement: +${fmt0(elecServings * electrolytePowderExample.k)} mg K, +${fmt0(
          elecServings * electrolytePowderExample.na
        )} mg Na (${elecServings} serving${elecServings === 1 ? '' : 's'}).`
      );
    }

    const saltSubServ = toNumber(saltSubServingsPerDay); // # of 1/4 tsp servings
    if (usesSaltSubstitute && saltSubServ > 0) {
      const saltSubK = saltSubServ * SALT_SUB_POTASSIUM_PER_QUARTER_TSP_MG;
      suppK += saltSubK;
      explanation.push(`Salt substitute (KCl): +${fmt0(saltSubK)} mg K (${saltSubServ} × 1/4 tsp).`);
    }

    // Totals
    const totalK = foodK + suppK;
    const totalNa = foodNa + suppNa;

    explanation.push(`==> TOTAL Estimated Intake: K = ${fmt0(totalK)} mg/day, Na = ${fmt0(totalNa)} mg/day.`);

    // Ratio & gaps
    const kNaRatio = totalNa > 0 ? totalK / totalNa : Infinity;
    explanation.push(`Potassium-to-Sodium Ratio (K:Na): ${fmt2(kNaRatio)} (Goal: ≥ 1.0, Ideally ≥ 1.5)`);

    const kGap = targetK_AI - totalK;
    const naExcess = totalNa - targetNa_CDRR;

    if (kGap > 0) explanation.push(`Potassium Gap: Estimated ${fmt0(kGap)} mg short of your K target.`);
    else explanation.push(`Potassium Intake: Appears adequate or above AI.`);

    if (naExcess > 0) explanation.push(`Sodium Excess: Estimated ${fmt0(naExcess)} mg OVER the Sodium CDRR limit.`);
    else explanation.push(`Sodium Intake: Appears within the CDRR limit.`);

    // Safety checks (educational)
    const hasCKDorACEARB = healthFlags.ckdStage3Plus || healthFlags.aceiArbMed || healthFlags.kSparingDiureticMed;

    if (hasCKDorACEARB && totalK > safetyThresholds.hyperkalemia_risk_k_mg) {
      safetyFlags.push({
        type: 'severe',
        text: `HYPERKALEMIA RISK: Your total potassium (~${fmt0(totalK)} mg) is high (> ${fmt0(
          safetyThresholds.hyperkalemia_risk_k_mg
        )} mg) AND you indicated CKD, ACE-I/ARB, or K-sparing diuretic use. Consult your clinician.`,
      });
    } else if (hasCKDorACEARB && suppK > 0) {
      safetyFlags.push({
        type: 'warning',
        text: `Caution: Potassium supplements (${fmt0(
          suppK
        )} mg from supplements) with CKD/ACE-I/ARB/K-sparing diuretics require medical supervision.`,
      });
    }

    if (healthFlags.ckdStage3Plus && suppK > safetyThresholds.ckd_k_supplement_cap_mg) {
      safetyFlags.push({
        type: 'warning',
        text: `High K supplement with CKD: ~${fmt0(
          suppK
        )} mg/day exceeds a conservative ${fmt0(safetyThresholds.ckd_k_supplement_cap_mg)} mg/day guideline. Discuss with your clinician.`,
      });
    }

    if (healthFlags.loopThiazideDiureticMed && totalNa < safetyThresholds.hyponatremia_risk_na_mg) {
      safetyFlags.push({
        type: 'warning',
        text: `Possible hyponatremia risk: Sodium intake ~${fmt0(totalNa)} mg (< ${fmt0(
          safetyThresholds.hyponatremia_risk_na_mg
        )} mg) while on loop/thiazide diuretics. Discuss with your clinician.`,
      });
    } else if (healthFlags.loopThiazideDiureticMed && kGap > 500) {
      safetyFlags.push({
        type: 'warning',
        text: `Possible hypokalemia risk: Potassium gap ~${fmt0(
          kGap
        )} mg while on loop/thiazide diuretics (can increase K loss).`,
      });
    }

    if (totalNa > safetyThresholds.excess_sodium_warn_mg) {
      safetyFlags.push({
        type: 'warning',
        text: `Excess sodium intake: ~${fmt0(totalNa)} mg (> ${fmt0(
          safetyThresholds.excess_sodium_warn_mg
        )} mg). Elevated cardiometabolic risk.`,
      });
    }

    // Guidance
    if (healthFlags.hypertension) {
      suggestions.general.push(
        'Hypertension noted: Aim for a higher K:Na ratio (≥1.5) by increasing potassium-rich foods and reducing high-sodium processed foods.'
      );
    }
    if (healthFlags.heartFailure) {
      suggestions.general.push(
        "Heart failure noted: Sodium and fluid balance are critical. Follow your clinician's specific advice on Na/K intake and monitoring."
      );
    }

    if (naExcess > 500 && kGap > 500) {
      suggestions.foodSwaps.push(
        'Swap: Replace 1 cup canned soup (≈ -800 mg Na, -150 mg K) with a large banana (≈ +420 mg K, -1 mg Na) and a cup of low-sodium broth.'
      );
      suggestions.foodSwaps.push('Swap: Reduce processed meats (≈ -600 mg Na/2 oz) and add 1/2 avocado (≈ +345 mg K).');
    } else if (naExcess > 500) {
      suggestions.foodSwaps.push('Reduce high-Na items: sauces, processed snacks, fast food, canned goods (unless low-sodium).');
    } else if (kGap > 500) {
      suggestions.foodSwaps.push('Boost K: fruits (bananas, oranges), vegetables (potatoes, spinach, tomatoes), legumes, and yogurt/milk.');
    }

    const sys = Number.parseInt(bpSys, 10);
    if (Number.isFinite(sys) && sys >= 130) {
      suggestions.general.push(
        `Elevated BP (${bpSys}/${bpDia}): Reducing sodium by ~1000 mg/day can lower systolic BP by ~5 mmHg. More dietary K may help.`
      );
    }

    // Results: keep numbers as numbers; format at render
    setCalculationResult({
      targetK: targetK_AI,
      targetNa: targetNa_CDRR,
      totalK,
      totalNa,
      kGap,
      naExcess,
      kNaRatio,
      explanation: explanation.join('\n'),
      suggestions,
      safetyFlags,
    });
    setShowResults(true);
  };

  return (
    <div className="potassiumSodiumCalculator">
      <h1>
        <span>
          <span role="img" aria-label="potassium and sodium balance">
            ⚖️
          </span>{' '}
          Potassium &amp; Sodium Balance Calculator
        </span>

        <span className="ai-promo-inline">
          <strong>Unlock Smarter Eating:</strong>{' '}
          Get a complete breakdown of vitamins, minerals, and fats plus a personalized
          weight-loss plan or nutrition tailored to your training with a single{' '}
          <a
            href="https://purehealthnexus.com/ai-nutrition-vitamins-minerals"
            className="ai-promo-link"
          >
            AI scan
          </a>.
        </span>
      </h1>


      <p className="important-note">
        <strong>Disclaimer:</strong> This tool estimates potassium and sodium intake from your inputs. It is not medical
        advice. Individual needs vary—especially with CKD, heart failure, hypertension, or medications (diuretics,
        ACE-inhibitors, ARBs). Discuss changes with your clinician.
      </p>

      <form onSubmit={calculatePotassiumSodiumBalance}>
        <h2>🧾 Your Information</h2>

        {/* Demographics */}
        <div className="form-section">
          <h3>👤 Demographics</h3>
          <label>
            Age (Years):*
            <input type="number" value={age} onChange={(e) => setAge(e.target.value)} required min={1} max={120} placeholder="e.g., 45" />
          </label>
          <label>
            Sex Assigned at Birth:*
            <select value={sex} onChange={(e) => setSex(e.target.value)} required>
              <option value="">-- Select --</option>
              <option value="female">Female</option>
              <option value="male">Male</option>
            </select>
          </label>
          {isFemale && (
            <>
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={isPregnant}
                  onChange={(e) => setIsPregnant(e.target.checked)}
                  disabled={isLactating || !isFemale || (Number.isFinite(ageNum) && (ageNum < 12 || ageNum > 55))}
                />
                Currently Pregnant?
              </label>
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={isLactating}
                  onChange={(e) => setIsLactating(e.target.checked)}
                  disabled={isPregnant || !isFemale || (Number.isFinite(ageNum) && (ageNum < 12 || ageNum > 55))}
                />
                Currently Lactating?
              </label>
            </>
          )}
        </div>

        {/* Health flags */}
        <div className="form-section">
          <h3>🩺 Health Conditions & Medications</h3>
          <fieldset>
            <legend>Check any that apply (affects safety/needs):</legend>
            <label className="checkbox-label indented">
              <input type="checkbox" name="hypertension" checked={healthFlags.hypertension} onChange={handleHealthFlagChange} />
              Diagnosed High Blood Pressure (Hypertension)?
            </label>
            <label className="checkbox-label indented alert-warning">
              <input type="checkbox" name="ckdStage3Plus" checked={healthFlags.ckdStage3Plus} onChange={handleHealthFlagChange} />
              Chronic Kidney Disease (CKD Stage 3 or higher)?
            </label>
            <label className="checkbox-label indented">
              <input type="checkbox" name="heartFailure" checked={healthFlags.heartFailure} onChange={handleHealthFlagChange} />
              Diagnosed Heart Failure?
            </label>
            <label className="checkbox-label indented alert-warning">
              <input type="checkbox" name="aceiArbMed" checked={healthFlags.aceiArbMed} onChange={handleHealthFlagChange} />
              Taking ACE Inhibitor or ARB (e.g., lisinopril, losartan)?
            </label>
            <label className="checkbox-label indented alert-warning">
              <input type="checkbox" name="kSparingDiureticMed" checked={healthFlags.kSparingDiureticMed} onChange={handleHealthFlagChange} />
              Taking K-sparing diuretic? (e.g., spironolactone, amiloride)
            </label>
            <label className="checkbox-label indented">
              <input
                type="checkbox"
                name="loopThiazideDiureticMed"
                checked={healthFlags.loopThiazideDiureticMed}
                onChange={handleHealthFlagChange}
              />
              Taking loop or thiazide diuretic? (e.g., furosemide, HCTZ)
            </label>
            <label className="checkbox-label indented">
              <input type="checkbox" name="nsaidsMed" checked={healthFlags.nsaidsMed} onChange={handleHealthFlagChange} />
              Regularly use NSAIDs? (e.g., ibuprofen, naproxen)
            </label>
          </fieldset>
        </div>

        {/* Activity */}
        <div className="form-section">
          <h3>🏃 Activity Level & Sweat</h3>
          <label>
            Typical Daily Intense Sweat Duration:
            <select value={activityLevel} onChange={(e) => setActivityLevel(e.target.value)}>
              <option value="low">Low (&lt; 1 hour intense sweat)</option>
              <option value="moderate">Moderate (1–2 hours intense sweat)</option>
              <option value="high">High (&gt; 2 hours intense sweat)</option>
            </select>
            <small>Shown as sodium replacement need; not counted as intake.</small>
          </label>
        </div>

        {/* Diet log */}
        <div className="form-section">
          <h3>🍽️ Estimated Daily Food Intake (Servings)</h3>
          <p>
            <small>Enter approximate number of servings per day. Decimals allowed (e.g., 0.5, 1.25).</small>
          </p>

          <h4>Potassium-Rich Foods:</h4>
          <div className="diet-grid k-na-diet-grid">
            <label>
              Bananas (medium):
              <input type="number" step="any" name="servings_banana" value={dietLog.servings_banana} onChange={handleDietChange} min="0" placeholder="e.g., 1" />
            </label>
            <label>
              Avocado (halves):
              <input type="number" step="any" name="servings_avocado" value={dietLog.servings_avocado} onChange={handleDietChange} min="0" placeholder="e.g., 0.5" />
            </label>
            <label>
              Potatoes (medium):
              <input type="number" step="any" name="servings_potato" value={dietLog.servings_potato} onChange={handleDietChange} min="0" placeholder="e.g., 1" />
            </label>
            <label>
              Spinach/Greens (cooked cups):
              <input type="number" step="any" name="servings_spinach" value={dietLog.servings_spinach} onChange={handleDietChange} min="0" placeholder="e.g., 1" />
            </label>
            <label>
              Beans/Lentils (cooked cups):
              <input type="number" step="any" name="servings_legumes" value={dietLog.servings_legumes} onChange={handleDietChange} min="0" placeholder="e.g., 1" />
            </label>
            <label>
              Yogurt/Milk (cups):
              <input type="number" step="any" name="servings_dairy" value={dietLog.servings_dairy} onChange={handleDietChange} min="0" placeholder="e.g., 2" />
            </label>
          </div>

          <h4>Sodium-Rich Foods / Habits:</h4>
          <div className="diet-grid k-na-diet-grid">
            <label>
              Canned Soup (cups):
              <input type="number" step="any" name="servings_canned_soup" value={dietLog.servings_canned_soup} onChange={handleDietChange} min="0" placeholder="e.g., 0" />
            </label>
            <label>
              Bread (slices):
              <input type="number" step="any" name="servings_bread" value={dietLog.servings_bread} onChange={handleDietChange} min="0" placeholder="e.g., 4" />
            </label>
            <label>
              Processed Meat (2 oz servings):
              <input type="number" step="any" name="servings_processed_meat" value={dietLog.servings_processed_meat} onChange={handleDietChange} min="0" placeholder="e.g., 1" />
            </label>
            <label>
              Cheese (slices/oz):
              <input type="number" step="any" name="servings_cheese" value={dietLog.servings_cheese} onChange={handleDietChange} min="0" placeholder="e.g., 2" />
            </label>
            <label>
              Fast Food Meals (per meal):
              <input type="number" step="any" name="servings_fast_food_meal" value={dietLog.servings_fast_food_meal} onChange={handleDietChange} min="0" placeholder="e.g., 0" />
            </label>
            <label>
              Salty Snacks (1 oz servings):
              <input type="number" step="any" name="servings_salty_snacks" value={dietLog.servings_salty_snacks} onChange={handleDietChange} min="0" placeholder="e.g., 1" />
            </label>
            <label>
              Meals Eaten Out / Takeaway (per meal):
              <input type="number" step="any" name="meals_eaten_out" value={dietLog.meals_eaten_out} onChange={handleDietChange} min="0" placeholder="e.g., 3" />
            </label>
            <label className="checkbox-label indented full-width">
              <input type="checkbox" checked={addsTableSalt} onChange={(e) => setAddsTableSalt(e.target.checked)} />
              Do you usually add salt to your food at the table?
            </label>
          </div>
        </div>

        {/* Supplements */}
        <div className="form-section">
          <h3>🧂 Supplements & Salt Substitutes</h3>
          <label className="checkbox-label">
            <input type="checkbox" checked={takesElectrolyteSupp} onChange={(e) => setTakesElectrolyteSupp(e.target.checked)} />
            Take electrolyte powders/drinks? (Containing Na/K)
          </label>
          {takesElectrolyteSupp && (
            <div className="supplement-details">
              <label>
                Servings per day:
                <input
                  type="number"
                  step="any"
                  value={electrolyteServings}
                  onChange={(e) => setElectrolyteServings(e.target.value)}
                  required={takesElectrolyteSupp}
                  min="0"
                  placeholder="e.g., 1"
                />
                <small>
                  (Example: 1 scoop ≈ {electrolytePowderExample.na} mg Na, {electrolytePowderExample.k} mg K)
                </small>
              </label>
            </div>
          )}

          <label className="checkbox-label">
            <input type="checkbox" checked={usesSaltSubstitute} onChange={(e) => setUsesSaltSubstitute(e.target.checked)} />
            Use salt substitute (Potassium Chloride - KCl)?
          </label>
          {usesSaltSubstitute && (
            <div className="supplement-details">
              <label>
                Servings per day (1/4 tsp each):
                <input
                  type="number"
                  step="any"
                  value={saltSubServingsPerDay}
                  onChange={(e) => setSaltSubServingsPerDay(e.target.value)}
                  required={usesSaltSubstitute}
                  min="0"
                  placeholder="e.g., 2"
                />
                <small>(Example: 1/4 tsp ≈ {SALT_SUB_POTASSIUM_PER_QUARTER_TSP_MG} mg K)</small>
              </label>
            </div>
          )}
        </div>

        {/* Optional labs/BP */}
        <div className="form-section">
          <h3>🩸 Optional Health Data (For Context)</h3>
          <label>
            Blood Pressure (Systolic/Diastolic):
            <div style={{ display: 'flex', gap: '10px' }}>
              <input type="number" value={bpSys} onChange={(e) => setBpSys(e.target.value)} placeholder="e.g., 135" style={{ width: '120px' }} /> /
              <input type="number" value={bpDia} onChange={(e) => setBpDia(e.target.value)} placeholder="e.g., 85" style={{ width: '120px' }} /> mmHg
            </div>
          </label>
          <label>
            Serum Sodium (mmol/L):
            <input type="number" step="any" value={serumNa} onChange={(e) => setSerumNa(e.target.value)} placeholder="e.g., 140" />
            <small>(~135–145 mmol/L)</small>
          </label>
          <label>
            Serum Potassium (mmol/L):
            <input type="number" step="any" value={serumK} onChange={(e) => setSerumK(e.target.value)} placeholder="e.g., 4.2" />
            <small>(~3.5–5.2 mmol/L)</small>
          </label>
        </div>

        <button type="submit">Calculate K:Na Balance</button>
      </form>

      {/* Results */}
      {showResults && calculationResult && (
        <div className="results-section">
          <h2>📊 Estimated Potassium & Sodium Balance</h2>

          {/* Safety Flags */}
          {calculationResult.safetyFlags.length > 0 && (
            <div className="safety-alerts">
              <h4>⚠️ Safety Alerts & Important Considerations</h4>
              <ul>
                {calculationResult.safetyFlags.map((flag, i) => (
                  <li key={i} className={`alert-${flag.type}`}>
                    <strong>{flag.type.toUpperCase()}:</strong> {flag.text}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Gauges & Ratio */}
          <div className="result-gauge-placeholder dual-gauge k-na-gauge">
            <div className="gauge-pair">
              <p>Potassium vs AI Target</p>
              <div
                className="gauge-bar"
                style={{
                  '--effective-value': calculationResult.totalK,
                  '--target-value': calculationResult.targetK,
                  '--max-value': Math.max(calculationResult.targetK * 1.5, calculationResult.totalK * 1.1),
                }}
              >
                <div className="gauge-fill"></div>
                <div className="gauge-target-line"></div>
              </div>
              <span className="gauge-label">
                Est: {fmt0(calculationResult.totalK)} mg / Target: {fmt0(calculationResult.targetK)} mg
              </span>
              <span className={`gauge-status status-${calculationResult.kGap > 0 ? 'low' : 'adequate'}`}>
                {calculationResult.kGap > 0 ? `(~${fmt0(calculationResult.kGap)} mg deficit)` : 'Adequate'}
              </span>
            </div>

            <div className="gauge-pair">
              <p>Sodium vs CDRR Limit</p>
              <div
                className="gauge-bar sodium-bar"
                style={{
                  '--effective-value': calculationResult.totalNa,
                  '--target-value': calculationResult.targetNa,
                  '--max-value': Math.max(calculationResult.targetNa * 2, calculationResult.totalNa * 1.1),
                }}
              >
                <div className="gauge-fill"></div>
                <div className="gauge-target-line"></div>
              </div>
              <span className="gauge-label">
                Est: {fmt0(calculationResult.totalNa)} mg / Limit: {fmt0(calculationResult.targetNa)} mg
              </span>
              <span className={`gauge-status status-${calculationResult.naExcess > 0 ? 'high' : 'adequate'}`}>
                {calculationResult.naExcess > 0 ? `(${fmt0(calculationResult.naExcess)} mg OVER)` : 'Within Limit'}
              </span>
            </div>

            <div className="ratio-display">
              <p>K:Na Ratio</p>
              <span
                className={`ratio-value ratio-${calculationResult.kNaRatio >= 1.5 ? 'ideal' : calculationResult.kNaRatio >= 1.0 ? 'good' : calculationResult.kNaRatio >= 0.8 ? 'low' : 'very-low'
                  }`}
              >
                {fmt2(calculationResult.kNaRatio)}
              </span>
              <small>(Goal ≥ 1.0, Ideal ≥ 1.5)</small>
            </div>
          </div>

          {/* Summary grid */}
          <div className="results-grid">
            <div className="result-item">
              <span className="result-label">Potassium AI:</span>
              <span className="result-value">{fmt0(calculationResult.targetK)} mg/day</span>
            </div>
            <div className="result-item">
              <span className="result-label">Sodium CDRR:</span>
              <span className="result-value">{fmt0(calculationResult.targetNa)} mg/day</span>
              <small className="result-note">(Limit)</small>
            </div>
            <div className="result-item">
              <span className="result-label">Est. Total K:</span>
              <span className="result-value significant">{fmt0(calculationResult.totalK)} mg/day</span>
            </div>
            <div className="result-item">
              <span className="result-label">Est. Total Na:</span>
              <span className="result-value significant">{fmt0(calculationResult.totalNa)} mg/day</span>
            </div>
            <div className="result-item">
              <span className="result-label">K Intake Status:</span>
              <span className={`result-value status-${calculationResult.kGap > 0 ? 'low' : 'adequate'}`}>
                {calculationResult.kGap > 0 ? `Deficit (~${fmt0(calculationResult.kGap)} mg)` : 'Adequate'}
              </span>
            </div>
            <div className="result-item">
              <span className="result-label">Na Intake Status:</span>
              <span className={`result-value status-${calculationResult.naExcess > 0 ? 'high' : 'adequate'}`}>
                {calculationResult.naExcess > 0 ? `Excess (~${fmt0(calculationResult.naExcess)} mg)` : 'Within Limit'}
              </span>
            </div>
            <div className="result-item">
              <span className="result-label">K:Na Ratio:</span>
              <span
                className={`result-value ratio-${calculationResult.kNaRatio >= 1.5 ? 'ideal' : calculationResult.kNaRatio >= 1.0 ? 'good' : calculationResult.kNaRatio >= 0.8 ? 'low' : 'very-low'
                  }`}
              >
                {fmt2(calculationResult.kNaRatio)}
              </span>
            </div>
          </div>

          {/* Guidance */}
          <h3>💡 Guidance & Suggestions</h3>
          {calculationResult.suggestions.foodSwaps.length > 0 && (
            <div className="suggestions-box">
              <h4>🍽️ Food Swap Ideas:</h4>
              <ul>{calculationResult.suggestions.foodSwaps.map((s, i) => <li key={`swap-${i}`}>{s}</li>)}</ul>
              <p>
                <small>Replace processed/packaged foods with whole foods like fruits, vegetables, and legumes.</small>
              </p>
            </div>
          )}
          {calculationResult.suggestions.supplement.length > 0 && (
            <div className="suggestions-box">
              <h4>🧂 Supplement Considerations (Discuss with your clinician first):</h4>
              <ul>{calculationResult.suggestions.supplement.map((s, i) => <li key={`supp-${i}`}>{s}</li>)}</ul>
            </div>
          )}
          {calculationResult.suggestions.general.length > 0 && (
            <div className="suggestions-box">
              <h4>⭐ General Health Notes:</h4>
              <ul>{calculationResult.suggestions.general.map((s, i) => <li key={`gen-${i}`}>{s}</li>)}</ul>
            </div>
          )}

          {/* Explanation */}
          <h3>📝 Calculation Explanation</h3>
          <pre className="explanation-box">{calculationResult.explanation}</pre>

          <p className="important-note">
            <strong>Reminder:</strong> Educational tool only. Actual needs depend on your medical history and labs.
            Discuss dietary changes or supplement use with your clinician.
          </p>

          <a href="#knak-education" className="scroll-link">
            Learn more about Potassium & Sodium below
          </a>
        </div>
      )}

      {/* Educational content */}
      <div id="knak-education" className="educational-section">
        <h2>Understanding Potassium & Sodium Balance</h2>

        <div className="content-block">
          <h3>1. Why Potassium & Sodium Matter</h3>
          <dl>
            <dt>Potassium (K⁺):</dt>
            <dd>
              <ul>
                <li>
                  <strong>Role:</strong> Primary intracellular cation; supports cell volume, nerve signals, muscle contraction (incl. heart),
                  vascular tone, insulin secretion, and acid–base balance.
                </li>
                <li>
                  <strong>Typical status:</strong> Most adults consume less than the Adequate Intake (AI).
                </li>
              </ul>
            </dd>
            <dt>Sodium (Na⁺):</dt>
            <dd>
              <ul>
                <li>
                  <strong>Role:</strong> Primary extracellular cation; fluid balance, plasma volume, nerve impulses, muscle function, nutrient
                  co-transport.
                </li>
                <li>
                  <strong>Typical status:</strong> Many adults exceed the CDRR, mostly from processed/restaurant foods.
                </li>
              </ul>
            </dd>
            <dt>The Balance (K:Na Ratio):</dt>
            <dd>
              <ul>
                <li>
                  <strong>Importance:</strong> Higher dietary potassium can blunt sodium’s BP-raising effect and is associated with lower CVD risk.
                </li>
                <li>
                  <strong>Goal:</strong> Aim for ≥1.0; ideal ≥1.5. Typical Western diets are &lt;1.0.
                </li>
              </ul>
            </dd>
          </dl>
        </div>

        <div className="content-block">
          <h3>2. Reference Intakes (2019 DRIs)</h3>
          <p>Based on National Academies of Sciences, Engineering, and Medicine (NASEM) 2019 report.</p>
          <table>
            <thead>
              <tr>
                <th>Life Stage Group</th>
                <th>Potassium AI (mg/day)</th>
                <th>Sodium CDRR (mg/day)</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>1–3 years</td>
                <td>{potassiumAITable['1-3y']}</td>
                <td>{sodiumCDRRTable['1-3y']}</td>
              </tr>
              <tr>
                <td>4–8 years</td>
                <td>{potassiumAITable['4-8y']}</td>
                <td>{sodiumCDRRTable['4-8y']}</td>
              </tr>
              <tr>
                <td>9–13 years</td>
                <td>{potassiumAITable['9-13y_male']}*</td>
                <td>{sodiumCDRRTable['9-13y']}</td>
              </tr>
              <tr>
                <td>14–18 years</td>
                <td>{potassiumAITable['14-18y_male']}*</td>
                <td>{sodiumCDRRTable['14-18y']}</td>
              </tr>
              <tr>
                <td>Men 19+ years</td>
                <td>{potassiumAITable['19+y_male']}</td>
                <td>{sodiumCDRRTable['19+y']}</td>
              </tr>
              <tr>
                <td>Women 19+ years</td>
                <td>{potassiumAITable['19+y_female']}</td>
                <td>{sodiumCDRRTable['19+y']}</td>
              </tr>
              <tr>
                <td>Pregnancy (19+)</td>
                <td>{potassiumAITable['pregnant_19+']}</td>
                <td>{sodiumCDRRTable['19+y']}</td>
              </tr>
              <tr>
                <td>Lactation (19+)</td>
                <td>{potassiumAITable['lactating_19+']}</td>
                <td>{sodiumCDRRTable['19+y']}</td>
              </tr>
            </tbody>
          </table>
          <small>
            *AI values shown for males 9–18y; female values may differ slightly depending on source interpretation. CDRR: Chronic Disease Risk
            Reduction Intake. AI: Adequate Intake.
          </small>
          <br />
          <small>
            <strong>Safety:</strong> No UL set for potassium from foods in healthy individuals. Supplemented/high intake can be risky with CKD,
            ACE-inhibitors/ARBs, or K-sparing diuretics (hyperkalemia). Sodium intakes above 2300 mg/day increase chronic disease risk.
          </small>
        </div>

        <div className="content-block">
          <h3>3. Groups Needing Special Consideration</h3>
          <ul>
            <li>
              <strong>Hypertension / CVD risk:</strong> Lower sodium toward CDRR; raise potassium toward AI (e.g., DASH pattern).
            </li>
            <li>
              <strong>CKD (Stage 3+):</strong> Reduced K excretion → hyperkalemia risk. Medical supervision essential, especially with K supplements
              or KCl salt substitutes.
            </li>
            <li>
              <strong>Medications:</strong> ACE-I/ARBs & K-sparing diuretics ↑ K; loop/thiazides ↑ Na & K losses.
            </li>
            <li>
              <strong>Heart failure:</strong> Sodium restriction and careful monitoring of Na/K with diuretics.
            </li>
            <li>
              <strong>Athletes / heavy sweaters:</strong> Higher sodium losses → consider activity-timed Na replacement; needs vary widely.
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
}

export default PotassiumSodiumCalculator;
